<script>
  import { stringify } from 'csv-stringify/browser/esm/sync';
  import { Button, Card, WidgetPlaceholder, Spinner } from 'flowbite-svelte';
  import { FileExportSolid } from 'flowbite-svelte-icons';
  import classNames from 'classnames';
  import { sendReportCsvExportDebugEvent } from '../data/debug';
  import { queryCuratedClicksSummary } from '../data/curatedClicksSummary';
  import { queryDailyCuratedStatsByPage } from '../data/dailyCuratedStats';
  import { getDailyResults } from '../data/utils';
  import { searchParams } from '../store';
  import { Heading } from '@button/ui';
  import CuratedPageChart from './CuratedClicksSummaryChart.svelte';
  import { queryCreatorCuratedPages } from '../data/creatorCuratedPages';

  $: curatedClicksSummary = queryCuratedClicksSummary({
    startDate: $searchParams.startDate,
    endDate: $searchParams.endDate,
    socialPlatform: $searchParams.socialPlatform,
  });
  $: curatedClicksSummaryData = $curatedClicksSummary?.isSuccess
    ? $curatedClicksSummary?.data
    : [];
  $: creatorCuratedPages = queryCreatorCuratedPages();
  $: creatorCuratedPagesData = $creatorCuratedPages?.isSuccess
    ? $creatorCuratedPages?.data
    : [];
  $: curatedPageStats =
    creatorCuratedPagesData.length > 0
      ? queryDailyCuratedStatsByPage({
          pages: creatorCuratedPagesData,
          startDate: $searchParams.startDate,
          endDate: $searchParams.endDate,
          socialPlatform: $searchParams.socialPlatform,
        })
      : null;

  $: curatedPageStatsIsLoading = $curatedPageStats?.some(
    (stats) => stats.isLoading,
  );
  $: isError = $curatedPageStats?.some((stats) => stats.isError);
  $: curatedPageStatsIsSuccess = $curatedPageStats?.every(
    (stats) => stats.isSuccess,
  );

  $: currentDailyResults = getDailyResults($curatedPageStats, 'currentData');
  $: previousDailyResults = getDailyResults($curatedPageStats, 'previousData');
  $: isLoading = curatedPageStatsIsLoading || $curatedClicksSummary?.isLoading;
  $: hasData =
    curatedPageStatsIsSuccess &&
    currentDailyResults?.length &&
    currentDailyResults?.some(
      (dailyResult) =>
        dailyResult?.total_page_views > 0 ||
        dailyResult?.total_shortlink_clicks > 0,
    );

  function handleClickExport() {
    const mappedResults = curatedClicksSummaryData.map((s) => {
      const { rank } = s;
      const { page, total_mobile_journeys: totalMobileJourneys } =
        s.curatedByPage;
      return {
        rank,
        page,
        mobile_clicks: totalMobileJourneys,
      };
    });

    const csv = stringify(mappedResults, {
      header: true,
      columns: [{ key: 'rank' }, { key: 'page' }, { key: 'mobile_clicks' }],
    });

    sendReportCsvExportDebugEvent('curated-clicks-summary');

    // Generate a hidden element to trigger file download
    const hiddenElement = window.document.createElement('a');
    hiddenElement.href = `data:text/csv;charset=utf-8,${encodeURI(csv)}`;
    hiddenElement.target = '_blank';
    hiddenElement.download = `curated_clicks_summary_${$searchParams.startDate}_${$searchParams.endDate}.csv`;
    hiddenElement.click();
  }
</script>

{#if isLoading || isError || hasData}
  <Card class="flex h-full w-full max-w-full flex-col">
    <Heading>CuratedBy Clicks Summary</Heading>
    {#if isLoading}
      <div class="relative">
        <div class="absolute flex h-full w-full items-center justify-center">
          <Spinner />
        </div>
        <WidgetPlaceholder divClass="w-full p-6" />
      </div>
    {/if}
    {#if isError}
      <div class="flex h-full flex-col items-center justify-center">
        <p class="text-gray-500">Something went wrong.</p>
        <button
          class="mt-4 rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700"
          on:click={() => $curatedClicksSummary.refetch()}
        >
          Retry
        </button>
      </div>
    {/if}
    {#if hasData}
      <div
        class={classNames('flex h-full flex-col', {
          hidden: !hasData,
        })}
      >
        <CuratedPageChart
          {currentDailyResults}
          {previousDailyResults}
          {curatedClicksSummaryData}
        />
        <div class="mt-4 flex flex-1 items-end">
          <Button
            on:click={handleClickExport}
            size="sm"
            color="light"
            class="w-fit"
            ><FileExportSolid class="me-2 h-3.5 w-3.5" />
            Export
          </Button>
        </div>
      </div>
    {/if}
  </Card>
{/if}
